/* c8 ignore start */
import store from '@/store';
import { createRouter, createWebHistory } from 'vue-router';

import analytics from '@/analytics';
import ContentLab from '@/studio/contentLab/ContentLab.vue';
import { UserRole } from '@/studio/settings/OrgMembersSettings/utils';
import { fireActionFromHash } from './routerHelpers';

function showNoWorkspacePage(store, to) {
  const isWorkspaceEnabled = store.state?.org?.currentOrg?.is_workspace_enabled;
  const currentUser = store.state.user.currentUser;
  const userRole = currentUser.role;
  const workspaces = currentUser.workspaces;

  if (
    isWorkspaceEnabled &&
    userRole === UserRole.WORKSPACE_ADMIN &&
    workspaces.length === 0 &&
    to.path !== '/no-workspace'
  ) {
    return true;
  }

  return false;
}

const routes = [
  {
    path: '/oauth/result',
    name: 'OAuthResult',
    component: () => import('@/studio/orgIntegration/solutionAuthentication/OAuthResult.vue')
  },
  {
    path: '/createorg',
    name: 'CreateOrg',
    component: () => import('@/studio/createOrg/CreateOrg.vue'),
    meta: { requiresAuth: true }
  },

  {
    path: '/registrants-activity',
    name: 'RegistrantsActivity',
    components: {
      default: () => import('@/studio/registrantsActivity/RegistrantsActivity.vue')
    }
  },
  {
    path: '/on-demand-embed-dummy/:eventId',
    name: 'OnDemandEmbedDummy',
    components: {
      default: () => import('@/studio/onDemandPage/OnDemandEmbedDummyPage.vue')
    }
  },
  {
    path: '/auth',
    components: {
      default: () => import('@/studio/auth/AuthWrapper.vue')
    },
    children: [
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/studio/auth/Login.vue')
      },
      {
        path: 'sso',
        name: 'Ssologin',
        component: () => import('@/studio/auth/OktaLogin.vue')
      },
      {
        path: 'oktalogin',
        name: 'Oktalogin',
        component: () => import('@/studio/auth/OktaLogin.vue')
      },
      {
        path: 'org/:orgId',
        name: 'OrgLogin',
        component: () => import('@/studio/auth/OrgLogin.vue')
      },
      {
        path: 'link/:email/:secretCode',
        name: `fromMagicLink`,
        component: () => import('@/studio/auth/FromMagicLink.vue')
      },
      {
        path: 'magicLinkSent',
        name: `magicLinkSent`,
        component: () => import('@/studio/auth/SentMagicLink.vue')
      },
      {
        path: 'reset',
        name: 'PasswordExpired',
        component: () => import('@/studio/auth/PasswordExpired.vue')
      },
      {
        path: 'forgot',
        name: 'ForgotPassword',
        component: () => import('@/studio/auth/ForgotPassword.vue')
      },
      {
        path: 'forgotPasswordSubmit',
        name: `ForgotPasswordSubmit`,
        component: () => import('@/studio/auth/ForgotPasswordSubmit.vue')
      },
      {
        path: 'confirmSignup',
        name: `ConfirmSignup`,
        component: () => import('@/studio/auth/ConfirmSignup.vue')
      },
      {
        path: 'external',
        name: `External`,
        component: () => import('@/studio/auth/External.vue')
      }
    ]
  },
  {
    path: '/content/auth',
    redirect: '/content/auth/login',
    components: {
      default: () => import('@/studio/contentLab/auth/ContentLabAuthWrapper.vue')
    },
    children: [
      {
        path: 'login',
        name: 'ContentLabLogin',
        component: () => import('@/studio/contentLab/auth/ContentLabLogin.vue')
      },
      {
        path: 'signup',
        name: 'ContentLabSignup',
        component: () => import('@/studio/contentLab/auth/ContentLabSignup.vue')
      },
      {
        path: 'forgot-password',
        name: 'ContentLabForgotPassword',
        component: () => import('@/studio/contentLab/auth/ContentLabForgotPassword')
      },
      {
        path: 'already-logged-in',
        name: 'ContentLabAlreadyLoggedIn',
        component: () => import('@/studio/contentLab/auth/ContentLabAlreadyLoggedIn.vue'),
        meta: { requiresAuth: true }
      }
    ]
  },
  {
    path: '/error',
    name: 'Error',
    component: () => import('@/views/Error.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/sponsor/:eventId/booth/:boothId/',
    name: 'SponsorsBooth',
    component: () => import('@/studio/booths/sponsorBooth/SponsorBooth.vue')
  },
  {
    path: '/no-workspace',
    name: 'NoWorkspace',
    component: () => import('@/studio/noWorkspace/NoWorkspace.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/',
    name: 'OrganizationWrapper',
    redirect: '/home',
    components: {
      default: () => import('@/layouts/Organization.vue')
    },
    props: true,
    children: [
      {
        path: '/metadata',
        name: 'MetadataPanel',
        component: () => import('@/views/MetadataPanel.vue')
      },
      {
        path: 'pages/:pageId/hub-page-builder',
        name: 'EventHubPageBuilder',
        component: () => import('@/studio/eventHubPages/eventHubPageDetails/EventHubPageBuilder.vue')
      },
      {
        path: 'eventHub',
        name: 'EventHub',
        redirect: '/pages',
        meta: { requiresAuth: true }
      },
      {
        path: 'eventHub/:eventHubId',
        name: 'EventHubBuilder',
        beforeEnter: (to, from, next) => {
          const eventHubId = to.params.eventHubId;
          next({ name: 'Page', params: { pageId: eventHubId } });
        },
        meta: { requiresAuth: true }
      },
      {
        path: 'pages',
        name: 'Pages',
        component: () => import('@/studio/eventHubPages/EventHubPages.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'pages/:pageId',
        name: 'Page',
        component: () => import('@/studio/eventHubPages/eventHubPageDetails/EventHubPageDetails.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'brand-kit',
        name: 'Brand-Kit',
        component: () => import('@/studio/brandKit/BrandKitList.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'brand-kit/new',
        name: 'CreateBrandKit',
        component: () => import('@/studio/brandKit/CreateEditBrandKitDialog.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'brand-kit/:brandKitId/edit',
        name: 'EditBrandKit',
        component: () => import('@/studio/brandKit/CreateEditBrandKitDialog.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'templates',
        name: 'Templates',
        component: () => import('@/studio/templateList/TemplateList.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'templates/:templateId',
        component: () => import('@/studio/templateLayout/TemplateLayout.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'TemplateDashboard',
            component: () => import('@/studio/templateDashboard/TemplateDashboard.vue')
          },
          {
            path: 'registration',
            name: 'TemplateRegistration',
            component: () => import('@/studio/templateRegistration/TemplateRegistrationWrapper.vue'),
            children: [
              {
                path: 'page',
                name: 'TemplateRegistrationPageAndForm',
                component: () => import('@/studio/templateRegistration/TemplateRegistration.vue'),
                meta: { requiresAuth: true }
              },
              {
                path: 'tracking-pixels',
                name: 'TemplateTrackingPixels',
                component: () =>
                  import(
                    /* webpackChunkName: "TrackingPixels" */ '@/studio/registration/RegistrationPageTrackingWrapper.vue'
                  )
              }
            ]
          },
          {
            path: 'registration/page-builder',
            name: 'TemplateRegistrationPageBuilder',
            component: () => import('@/studio/templateRegistration/TemplateRegistrationPageBuilder.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'on-demand-page-builder',
            name: 'TemplateOnDemandPageBuilder',
            component: () => import('@/studio/onDemandPage/template/TemplateOnDemandBuilder.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'look-and-feel',
            name: 'TemplateLookAndFeel',
            component: () => import('@/studio/lookAndFeel/LookAndFeelDrawer.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'staff',
            name: 'TemplateStaff',
            component: () => import('@/studio/templateStaff/TemplateStaff.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'email/templates',
            name: 'TemplateEmailTemplates',
            component: () => import('@/studio/emailTemplates/EmailTemplates.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'email/schedule',
            name: 'TemplateEmailSchedule',
            component: () =>
              import(/* webpackChunkName: "RegistrationRegistrants" */ '@/studio/emailSchedule/EmailSchedule.vue')
          },
          {
            path: 'professional-education',
            name: 'ProfessionalEducation',
            component: () =>
              import(
                /* webpackChunkName: "ProfessionalEducation" */ '@/studio/professionalEducation/ProfessionalEducation.vue'
              ),
            beforeEnter: (to, from, next) => {
              if (!store.getters['org/getCurrentOrg']?.enableCertificateProfessionalEducation) {
                return next({ name: 'TemplateDashboard', params: to.params });
              }
              next();
            }
          },
          {
            path: 'professional-education/rule-set',
            name: 'RuleSetEngine',
            component: () =>
              import(/* webpackChunkName: "RuleSetEngine" */ '@/studio/professionalEducation/RuleSetEngineModal.vue'),
            beforeEnter: (to, from, next) => {
              if (!store.getters['org/getCurrentOrg']?.enableCertificateProfessionalEducation) {
                return next({ name: 'TemplateDashboard', params: to.params });
              }
              next();
            }
          },
          {
            path: 'integration',
            name: 'TemplateIntegration',
            redirect: {
              name: 'TemplateIntegrationInfo'
            },
            component: () =>
              import(
                /* webpackChunkName: "TemplateIntegrationWrapper" */ '@/studio/templateIntegration/TemplateIntegrationWrapper.vue'
              ),
            children: [
              {
                path: 'info',
                name: 'TemplateIntegrationInfo',
                component: () =>
                  import(
                    /* webpackChunkName: "TemplateIntegrationInfo" */ '@/studio/templateIntegration/TemplateIntegrationInfo.vue'
                  )
              },
              {
                path: ':integrationType',
                name: 'TemplateIntegrationDashboard',
                component: () =>
                  import(
                    /* webpackChunkName: "TemplateIntegrationDashboard" */ '@/studio/templateIntegration/TemplateIntegrationDashboard.vue'
                  ),
                children: [
                  {
                    path: 'settings',
                    name: 'TemplateIntegrationSettings',
                    component: () =>
                      import(
                        /* webpackChunkName: "TemplateIntegrationSettings" */ '@/studio/templateIntegration/TemplateIntegrationSettings.vue'
                      )
                  },
                  {
                    path: 'field-mapping/:crmObjectType',
                    name: 'TemplateFieldMapping',
                    component: () =>
                      import(
                        /* webpackChunkName: "TemplateFieldMapping" */ '@/studio/templateIntegration/fieldMapping/TemplateFieldMapping.vue'
                      )
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: 'events',
        name: 'Events',
        component: () => import('@/studio/studioEventList/StudioEventListPage.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/recordings',
        name: 'Recordings',
        component: () => import('@/studio/studioEventList/StudioEventListPage.vue'),
        meta: { requiresAuth: true },
        props: () => ({ recordingsOnly: true }) // Pass custom props
      },
      {
        path: '/series',
        name: 'SeriesList',
        component: () => import('@/studio/series/studioSeriesList/StudioSeriesList.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/series/:seriesId',
        name: 'Series',
        components: {
          default: () => import('@/studio/series/SeriesDetailsLayout/SeriesDetailsLayout.vue')
        },
        children: [
          {
            path: '',
            name: 'SeriesDashboard',
            component: () => import('@/studio/series/seriesDashboard/SeriesDashboard.vue')
          },
          {
            path: 'events',
            name: 'SeriesEventsWrapper',
            component: () => import('@/studio/series/studioSeriesEventsList/StudioSeriesEventsPage.vue')
          },
          {
            path: 'integration',
            name: 'SeriesIntegration',
            component: () => import('@/studio/seriesIntegration/SeriesIntegration.vue'),
            children: [
              {
                path: ':integrationType',
                name: 'SeriesIntegrationSettings',
                component: () =>
                  import(
                    /* webpackChunkName: "SeriesIntegrationSettings" */ '@/studio/seriesIntegration/SeriesIntegrationSettings.vue'
                  )
              }
            ]
          },
          {
            path: 'reports',
            name: 'Reports',
            component: () =>
              import(/* webpackChunkName: "SeriesReports" */ '@/studio/seriesReports/SeriesReportsWrapper.vue'),
            children: [
              {
                path: 'summary',
                name: 'SeriesSummary',
                component: () =>
                  import(/* webpackChunkName: "SeriesSummary" */ '@/studio/seriesReports/SeriesSummary.vue')
              },
              {
                path: 'event/:id',
                name: 'SeriesEventSummaryDashboard',
                component: () =>
                  import(
                    /* webpackChunkName: "SeriesEventSummaryDashboard" */ '@/studio/seriesReports/SeriesEventSummaryDashboard.vue'
                  )
              }
            ]
          },
          {
            path: 'registration',
            name: 'SeriesRegistration',
            component: () =>
              import(
                /* webpackChunkName: "SeriesRegistration" */ '@/studio/series/registration/SeriesRegistrationWrapper.vue'
              ),
            children: [
              {
                path: 'page',
                name: 'SeriesRegistrationPageAndForm',
                component: () =>
                  import(
                    /* webpackChunkName: "SeriesRegistrationPageAndForm" */ '@/studio/series/registration/page/SeriesRegistrationPageWrapper.vue'
                  )
              },
              {
                path: 'page/page-builder',
                name: 'SeriesRegistrationPageBuilder',
                component: () => import('@/studio/series/registration/page/SeriesRegistrationPageBuilder.vue')
              },
              {
                path: 'registrants',
                name: 'SeriesRegistrants',
                component: () =>
                  import(
                    /* webpackChunkName: "SeriesRegistrants" */ '@/studio/series/registration/registrants/SeriesRegistrantsWrapper.vue'
                  )
              },
              {
                path: 'tracking-pixels',
                name: 'SeriesTrackingPixels',
                component: () =>
                  import(
                    /* webpackChunkName: "TrackingPixels" */ '@/studio/registration/RegistrationPageTrackingWrapper.vue'
                  )
              }
            ]
          },
          {
            path: 'email/schedule',
            name: 'SeriesEmailSchedule',
            component: () =>
              import(/* webpackChunkName: "SeriesEmailSchedule" */ '@/studio/emailSchedule/EmailSchedule.vue')
          },
          {
            path: 'email/templates',
            name: 'SeriesEmailTemplates',
            component: () =>
              import(/* webpackChunkName: "SeriesEmailTemplates" */ '@/studio/emailTemplates/EmailTemplates.vue')
          }
        ]
      },
      {
        path: '/repeating-event/:seriesId',
        name: 'RepeatingEvent',
        components: {
          default: () => import('@/studio/series/SeriesDetailsLayout/SeriesDetailsLayout.vue')
        },
        children: [
          {
            path: '',
            name: 'RepeatingEventDashboard',
            component: () => import('@/studio/series/seriesDashboard/SeriesDashboard.vue')
          },
          {
            path: 'events',
            name: 'RepeatingEventEventsWrapper',
            component: () => import('@/studio/series/studioSeriesEventsList/StudioSeriesEventsPage.vue')
          },
          {
            path: 'integration',
            name: 'RepeatingEventIntegration',
            component: () => import('@/studio/eventIntegration/EventIntegration.vue'),
            children: [
              {
                path: ':integrationType',
                name: 'RepeatingEventIntegrationSettings',
                component: () =>
                  import(
                    /* webpackChunkName: "RepeatingEventIntegrationSettings" */ '@/studio/eventIntegration/EventIntegrationSettings.vue'
                  )
              }
            ]
          },
          {
            path: 'reports',
            name: 'RepeatingEventReports',
            component: () =>
              import(/* webpackChunkName: "RepeatingEventReports" */ '@/studio/seriesReports/SeriesReportsWrapper.vue'),
            children: [
              {
                path: 'summary',
                name: 'RepeatingEventSummary',
                component: () =>
                  import(/* webpackChunkName: "RepeatingEventSummary" */ '@/studio/seriesReports/SeriesSummary.vue')
              },
              {
                path: 'event/:id',
                name: 'RepeatingEventEventSummaryDashboard',
                component: () =>
                  import(
                    /* webpackChunkName: "RepeatingEventEventSummaryDashboard" */ '@/studio/seriesReports/SeriesEventSummaryDashboard.vue'
                  )
              }
            ]
          },
          {
            path: 'registration',
            name: 'RepeatingEventRegistration',
            component: () =>
              import(
                /* webpackChunkName: "RepeatingEventRegistration" */ '@/studio/series/registration/SeriesRegistrationWrapper.vue'
              ),
            children: [
              {
                path: 'page',
                name: 'RepeatingEventRegistrationPageAndForm',
                component: () =>
                  import(
                    /* webpackChunkName: "RepeatingEventRegistrationPageAndForm" */ '@/studio/series/registration/page/SeriesRegistrationPageWrapper.vue'
                  )
              },
              {
                path: 'page/page-builder',
                name: 'RepeatingEventRegistrationPageBuilder',
                component: () =>
                  import(
                    /* webpackChunkName: "RepeatingEventRegistrationPageBuilder" */ '@/studio/registration/page/RegistrationPageBuilder.vue'
                  )
              },
              {
                path: 'registrants',
                name: 'RepeatingEventRegistrants',
                component: () =>
                  import(
                    /* webpackChunkName: "RepeatingEventRegistrants" */ '@/studio/series/registration/registrants/SeriesRegistrantsWrapper.vue'
                  )
              },
              {
                path: 'tracking-pixels',
                name: 'RepeatingEventTrackingPixels',
                component: () =>
                  import(
                    /* webpackChunkName: "RepeatingEventTrackingPixels" */ '@/studio/registration/RegistrationPageTrackingWrapper.vue'
                  )
              }
            ]
          },
          {
            path: 'on-demand-page-builder',
            name: 'RepeatingEventOnDemandPageBuilder',
            component: () => import('@/studio/onDemandPage/repeatingEvent/RepeatingEventOnDemandBuilder.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'email/schedule',
            name: 'RepeatingEventEmailSchedule',
            component: () =>
              import(/* webpackChunkName: "RepeatingEventEmailSchedule" */ '@/studio/emailSchedule/EmailSchedule.vue')
          },
          {
            path: 'email/templates',
            name: 'RepeatingEventEmailTemplates',
            component: () =>
              import(
                /* webpackChunkName: "RepeatingEventEmailTemplates" */ '@/studio/emailTemplates/EmailTemplates.vue'
              )
          },
          {
            path: 'look-and-feel',
            name: 'RepeatingEventLookAndFeel',
            component: () =>
              import(/* webpackChunkName: "RepeatingEventLookAndFeel" */ '@/studio/lookAndFeel/LookAndFeelDrawer.vue')
          },
          {
            path: 'programs',
            name: 'RepeatingEventPrograms',
            component: () => import(/* webpackChunkName: "RepeatingEventPrograms" */ '@/studio/ProgramWrapper.vue'),
            children: [
              {
                path: 'agenda',
                name: 'RepeatingEventAgenda',
                component: () => import(/* webpackChunkName: "RepeatingEventAgenda" */ '@/studio/agenda/Agenda.vue')
              },
              {
                path: 'speakers',
                name: 'RepeatingEventSpeakers',
                component: () =>
                  import(/* webpackChunkName: "RepeatingEventSpeakers" */ '@/studio/speakers/Speakers.vue')
              },
              {
                path: 'staff',
                name: 'RepeatingEventStaff',
                component: () => import(/* webpackChunkName: "RepeatingEventStaff" */ '@/studio/staff/Staff.vue')
              },
              {
                path: 'check-in-staff',
                name: 'RepeatingEventCheckInStaff',
                component: () =>
                  import(/* webpackChunkName: "RepeatingEventCheckInStaff" */ '@/studio/checkInStaff/CheckInStaff.vue')
              },
              {
                path: 'rooms',
                name: 'RepeatingEventRooms',
                component: () => import(/* webpackChunkName: "RepeatingEventRooms" */ '@/studio/rooms/Rooms.vue')
              },
              {
                path: 'booths',
                name: 'RepeatingEventBooths',
                component: () => import(/* webpackChunkName: "RepeatingEventBooths" */ '@/studio/booths/Booths.vue')
              }
            ]
          }
        ]
      },
      {
        path: '/recording-event/:eventId',
        name: 'RecordingEvent',
        component: () =>
          import(/* webpackChunkName: "RecordingStudioLayout" */ '@/studio/recordingStudio/RecordingStudioLayout.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: '/webinar/:eventId',
        name: 'Webinar',
        component: () => import(/* webpackChunkName: "WebinarLayout" */ '@/studio/webinar/WebinarLayout.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: 'on-demand-page-builder',
            name: 'OnDemandPageBuilder',
            component: () => import('@/studio/onDemandPage/OnDemandBuilder.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'on-demand-embed-builder',
            name: 'OnDemandEmbedBuilder',
            component: () => import('@/studio/onDemandPage/OnDemandBuilder.vue'),
            meta: { requiresAuth: true }
          },
          {
            path: 'details',
            name: 'WebinarDetails',
            component: () =>
              import(
                /* webpackChunkName: "WebinarDetailsSection" */ '@/studio/webinar/sections/WebinarDetailsSection.vue'
              ),
            children: [
              {
                path: 'videos',
                name: 'WebinarContentVideos',
                component: () =>
                  import(
                    /* webpackChunkName: "ContentVideosSection" */ '@/studio/webinar/sections/contentSection/ContentVideosSection.vue'
                  )
              },
              {
                path: 'social',
                name: 'WebinarContentSocial',
                component: () =>
                  import(
                    /* webpackChunkName: "ContentSocialSection" */ '@/studio/webinar/sections/contentSection/ContentSocialSection.vue'
                  )
              },
              {
                path: 'email',
                name: 'WebinarContentEmail',
                component: () =>
                  import(
                    /* webpackChunkName: "ContentEmailSection" */ '@/studio/webinar/sections/contentSection/ContentEmailSection.vue'
                  )
              },
              {
                path: 'blog',
                name: 'WebinarContentBlog',
                component: () =>
                  import(
                    /* webpackChunkName: "ContentBlogSection" */ '@/studio/webinar/sections/contentSection/ContentBlogSection.vue'
                  )
              },
              {
                path: 'takeaways',
                name: 'WebinarContentTakeaways',
                component: () =>
                  import(
                    /* webpackChunkName: "ContentTakeawaysSection" */ '@/studio/webinar/sections/contentSection/ContentTakeawaysSection.vue'
                  )
              },
              {
                path: 'transcript',
                name: 'WebinarContentTranscript',
                component: () =>
                  import(
                    /* webpackChunkName: "ContentTranscriptSection" */ '@/studio/webinar/sections/contentSection/ContentTranscriptSection.vue'
                  )
              },
              {
                path: 'staff',
                name: 'WebinarStaff',
                component: () =>
                  import(
                    /* webpackChunkName: "WebinarStaffSection" */ '@/studio/webinar/sections/staff/StaffSection.vue'
                  )
              }
            ]
          },
          {
            path: 'page',
            name: 'WebinarRegistrationPage',
            component: () =>
              import(
                /* webpackChunkName: "WebinarRegistrationPage" */ '@/studio/webinar/sections/registration/WebinarRegistrationPage.vue'
              )
          },
          {
            path: 'registration',
            name: 'WebinarRegistration',
            component: () =>
              import(
                /* webpackChunkName: "WebinarRegistration" */ '@/studio/webinar/sections/registration/WebinarRegistration.vue'
              ),
            children: [
              {
                path: 'registrants',
                name: 'WebinarRegistrants',
                component: () =>
                  import(
                    /* webpackChunkName: "WebinarRegistrants" */ '@/studio/webinar/sections/registration/WebinarRegistrants.vue'
                  )
              },
              {
                path: 'tracking-pixels',
                name: 'WebinarTrackingPixelsAndTags',
                component: () =>
                  import(
                    /* webpackChunkName: "WebinarTrackingPixelsAndTags" */ '@/studio/webinar/sections/registration/WebinarTrackingPixelsAndTags.vue'
                  )
              },
              {
                path: 'form',
                name: 'WebinarRegistrationForm',
                component: () =>
                  import(
                    /* webpackChunkName: "WebinarRegistrationForm" */ '@/studio/webinar/sections/registration/WebinarRegistrationForm.vue'
                  )
              }
            ]
          },
          {
            path: 'email',
            name: 'WebinarEmails',
            component: () =>
              import(/* webpackChunkName: "WebinarEmails" */ '@/studio/webinar/sections/emails/WebinarEmails.vue'),
            children: [
              {
                path: 'schedule',
                name: 'WebinarEmailSchedule',
                component: () =>
                  import(
                    /* webpackChunkName: "WebinarEmailSchedule" */ '@/studio/webinar/sections/emails/schedule/WebinarEmailSchedule.vue'
                  )
              },
              {
                path: 'templates',
                name: 'WebinarEmailTemplates',
                component: () =>
                  import(
                    /* webpackChunkName: "WebinarEmailTemplates" */ '@/studio/webinar/sections/emails/templates/WebinarEmailTemplates.vue'
                  )
              }
            ]
          },
          {
            path: 'analytics',
            name: 'WebinarAnalytics',
            component: () =>
              import(
                /* webpackChunkName: "WebinarAnalyticsSection" */ '@/studio/webinar/sections/WebinarAnalyticsSection.vue'
              )
          },
          {
            path: 'integration',
            name: 'WebinarIntegrationNew',
            redirect: {
              name: 'WebinarIntegrationSummary'
            },
            component: () =>
              import(
                /* webpackChunkName: "WebinarIntegrationSummary" */ '@/studio/eventIntegration/EventIntegrationWrapper.vue'
              ),
            children: [
              {
                path: 'summary',
                name: 'WebinarIntegrationSummary',
                component: () =>
                  import(
                    /* webpackChunkName: "WebinarIntegrationSummary" */ '@/studio/eventIntegration/EventIntegrationSummary.vue'
                  )
              },
              {
                path: ':integrationType',
                name: 'WebinarIntegrationDashboard',
                component: () =>
                  import(
                    /* webpackChunkName: "WebinarIntegrationDashboard" */ '@/studio/eventIntegration/EventIntegrationDashboard.vue'
                  ),
                children: [
                  {
                    path: 'settings',
                    name: 'WebinarIntegrationSettingsNew',
                    component: () =>
                      import(
                        /* webpackChunkName: "WebinarIntegrationSettingsNew" */ '@/studio/eventIntegration/EventIntegrationSettings.vue'
                      )
                  },
                  {
                    path: 'field-mapping/:crmObjectType',
                    name: 'WebinarFieldMapping',
                    component: () =>
                      import(
                        /* webpackChunkName: "WebinarFieldMapping" */ '@/studio/orgIntegration/fieldMapping/FieldMapping.vue'
                      )
                  }
                ]
              }
            ]
          },
          {
            path: 'look-and-feel',
            name: 'WebinarLookAndFeel',
            component: () =>
              import(
                /* webpackChunkName: "WebinarLookAndFeel" */ '@/studio/webinar/sections/lookAndFeel/WebinarLookAndFeelWrapper.vue'
              )
          },
          {
            path: 'professional-education',
            name: 'WebinarProfessionalEducation',
            component: () =>
              import(
                /* webpackChunkName: "WebinarProfessionalEducation" */ '@/studio/professionalEducation/ProfessionalEducationWrapper.vue'
              ),
            beforeEnter: (to, from, next) => {
              if (!store.getters['org/getCurrentOrg']?.enableCertificateProfessionalEducation) {
                return next({ name: 'WebinarDetails', params: to.params });
              }
              next();
            }
          },
          {
            path: 'professional-education/rule-set',
            name: 'WebinarRuleSetEngine',
            component: () =>
              import(
                /* webpackChunkName: "WebinarRuleSetEngine" */ '@/studio/professionalEducation/RuleSetEngineModal.vue'
              ),
            beforeEnter: (to, from, next) => {
              if (!store.getters['org/getCurrentOrg']?.enableCertificateProfessionalEducation) {
                return next({ name: 'WebinarDetails', params: to.params });
              }
              next();
            }
          }
        ]
      },
      {
        path: '/content',
        name: 'ContentLab',
        component: ContentLab,
        meta: { requiresAuth: true }
      },
      {
        path: '/content/:param+',
        name: 'ContentLabChildren',
        component: ContentLab,
        meta: { requiresAuth: true }
      },
      {
        path: '/events/:eventId',
        components: {
          default: () => import('@/studio/EventDetailsLayout/EventDetailsLayout.vue')
        },
        beforeEnter: (to, from, next) => {
          store.commit('studioEventList/setCurrentEventID', to.params.eventId);
          next();
        },
        children: [
          {
            path: '',
            name: 'EventDashboard',
            component: () => import('@/studio/eventDashboard/EventDashboard.vue')
          },
          {
            path: 'recordings-new',
            name: 'RecordingsNew',
            component: () => import('@/studio/recordings/Recordings.vue')
          },
          {
            path: 'registration-new',
            name: 'RegistrationNew',
            component: () =>
              import(/* webpackChunkName: "Registration" */ '@/studio/registration/RegistrationWrapper.vue'),
            children: [
              {
                path: 'page',
                name: 'RegistrationPageAndForm',
                component: () =>
                  import(
                    /* webpackChunkName: "RegistrationPage" */ '@/studio/registration/page/RegistrationPageWrapper.vue'
                  )
              },
              {
                path: 'page/page-builder',
                name: 'RegistrationPageBuilder',
                component: () =>
                  import(
                    /* webpackChunkName: "RegistrationPageBuilder" */ '@/studio/registration/page/RegistrationPageBuilder.vue'
                  )
              },
              {
                path: 'registrants',
                name: 'RegistrantsNew',
                component: () =>
                  import(/* webpackChunkName: "Registrants" */ '@/studio/eventRegistrants/EventRegistrants.vue')
              },
              {
                path: 'tracking-pixels',
                name: 'EventTrackingPixels',
                component: () =>
                  import(
                    /* webpackChunkName: "TrackingPixels" */ '@/studio/registration/RegistrationPageTrackingWrapper.vue'
                  )
              }
            ]
          },
          {
            path: 'programs',
            name: 'Programs',
            component: () => import(/* webpackChunkName: "Registration" */ '@/studio/ProgramWrapper.vue'),
            children: [
              {
                path: 'agenda',
                name: 'Agenda',
                component: () => import(/* webpackChunkName: "RegistrationPage" */ '@/studio/agenda/Agenda.vue')
              },
              {
                path: 'speakers',
                name: 'Speakers',
                component: () => import(/* webpackChunkName: "RegistrationPage" */ '@/studio/speakers/Speakers.vue')
              },
              {
                path: 'staff',
                name: 'Staff',
                component: () => import(/* webpackChunkName: "Staff" */ '@/studio/staff/Staff.vue')
              },
              {
                path: 'check-in-staff',
                name: 'CheckInStaff',
                component: () => import('@/studio/checkInStaff/CheckInStaff.vue')
              },
              {
                path: 'rooms',
                name: 'Rooms',
                component: () => import(/* webpackChunkName: "Registrants" */ '@/studio/rooms/Rooms.vue')
              },
              {
                path: 'booths',
                name: 'Booths',
                component: () => import(/* webpackChunkName: "Registrants" */ '@/studio/booths/Booths.vue')
              }
            ]
          },
          {
            path: 'look-and-feel',
            name: 'LookAndFeel',
            component: () => import(/* webpackChunkName: "LookAndFeel" */ '@/studio/lookAndFeel/LookAndFeelDrawer.vue')
          },
          {
            path: 'analytics-new',
            name: 'AnalyticsNew',
            component: () => import(/* webpackChunkName: "AnalyticsNew" */ '@/studio/Analytics/AnalyticsWrapper.vue'),
            children: [
              {
                path: 'summary',
                name: 'EventSummaryDashboard',
                component: () =>
                  import(/* webpackChunkName: "EventSummaryDashboard" */ '@/studio/Analytics/Analytics.vue')
              },
              {
                path: 'pre-event',
                name: 'PreEventDashboard',
                component: () => import(/* webpackChunkName: "PreEventDashboard" */ '@/studio/Analytics/Analytics.vue')
              },
              {
                path: 'engagement',
                name: 'EngagementDashboard',
                component: () =>
                  import(/* webpackChunkName: "EngagementDashboard" */ '@/studio/Analytics/Analytics.vue')
              },
              {
                path: 'sessions',
                name: 'SessionsDashboard',
                component: () => import(/* webpackChunkName: "SessionsDashboard" */ '@/studio/Analytics/Analytics.vue')
              },
              {
                path: 'booths',
                name: 'BoothsDashboard',
                component: () => import(/* webpackChunkName: "BoothsDashboard" */ '@/studio/Analytics/Analytics.vue')
              },
              {
                path: 'rooms',
                name: 'RoomsDashboard',
                component: () => import(/* webpackChunkName: "RoomsDashboard" */ '@/studio/Analytics/Analytics.vue')
              },
              {
                path: 'emails',
                name: 'EmailsDashboard',
                component: () => import(/* webpackChunkName: "EmailsDashboard" */ '@/studio/Analytics/Analytics.vue')
              }
            ]
          },
          {
            path: 'email/templates',
            name: 'EmailTemplates',
            component: () =>
              import(/* webpackChunkName: "RegistrationRegistrants" */ '@/studio/emailTemplates/EmailTemplates.vue')
          },
          {
            path: 'email/schedule',
            name: 'EmailSchedule',
            component: () =>
              import(/* webpackChunkName: "RegistrationRegistrants" */ '@/studio/emailSchedule/EmailSchedule.vue')
          },
          {
            path: 'professional-education',
            name: 'EventProfessionalEducation',
            component: () =>
              import(
                /* webpackChunkName: "EventProfessionalEducation" */ '@/studio/professionalEducation/ProfessionalEducationWrapper.vue'
              ),
            beforeEnter: (to, from, next) => {
              if (!store.getters['org/getCurrentOrg']?.enableCertificateProfessionalEducation) {
                return next({ name: 'EventDashboard', params: to.params });
              }
              next();
            }
          },
          {
            path: 'professional-education/rule-set',
            name: 'EventRuleSetEngine',
            component: () =>
              import(
                /* webpackChunkName: "EventRuleSetEngine" */ '@/studio/professionalEducation/RuleSetEngineModal.vue'
              ),
            beforeEnter: (to, from, next) => {
              if (!store.getters['org/getCurrentOrg']?.enableCertificateProfessionalEducation) {
                return next({ name: 'EventDashboard', params: to.params });
              }
              next();
            }
          },
          {
            path: 'integration',
            name: 'EventIntegrationNew',
            redirect: {
              name: 'EventIntegrationSummary'
            },
            component: () =>
              import(
                /* webpackChunkName: "EventIntegrationWrapper" */ '@/studio/eventIntegration/EventIntegrationWrapper.vue'
              ),
            children: [
              {
                path: 'summary',
                name: 'EventIntegrationSummary',
                component: () =>
                  import(
                    /* webpackChunkName: "EventIntegrationSummary" */ '@/studio/eventIntegration/EventIntegrationSummary.vue'
                  )
              },
              {
                path: ':integrationType',
                name: 'EventIntegrationDashboard',
                component: () =>
                  import(
                    /* webpackChunkName: "EventIntegrationDashboard" */ '@/studio/eventIntegration/EventIntegrationDashboard.vue'
                  ),
                children: [
                  {
                    path: 'settings',
                    name: 'EventIntegrationSettingsNew',
                    component: () =>
                      import(
                        /* webpackChunkName: "EventIntegrationSettingsNew" */ '@/studio/eventIntegration/EventIntegrationSettings.vue'
                      )
                  },
                  {
                    path: 'field-mapping/:crmObjectType',
                    name: 'EventFieldMapping',
                    component: () =>
                      import(
                        /* webpackChunkName: "EventFieldMapping" */ '@/studio/orgIntegration/fieldMapping/FieldMapping.vue'
                      )
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: 'home',
        name: 'OrgHome',
        component: () => import('@/studio/dashboard/Dashboard.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: 'upcoming-events',
            name: 'UpcomingEvents',
            redirect: '/events'
          },
          {
            path: 'past-events',
            name: 'PastEvents',
            redirect: '/events'
          }
        ]
      },
      {
        path: 'organization-team',
        name: 'OrgTeam',
        redirect: '/settings-new/users',
        meta: { requiresAuth: true }
      },
      {
        path: 'videos',
        name: 'Videos',
        component: () => import('@/studio/videoLibrary/VideoLibraryPage.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'library',
        name: 'Library',
        redirect: '/events',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'imageLibrary',
            name: 'ImageLibrary',
            redirect: '/events'
          },
          {
            path: 'videoLibrary',
            name: 'VideoLibrary',
            redirect: '/events'
          },
          {
            path: 'slides',
            name: 'Slides',
            redirect: '/events'
          },
          {
            path: 'email-template',
            name: 'EmailTemplate',
            redirect: '/events'
          },
          {
            path: 'email-template/create-template',
            name: 'CreateTemplate',
            redirect: '/events'
          },
          {
            path: 'email-template/update-template/:templateId',
            name: 'UpdateTemplate',
            redirect: '/events'
          },
          {
            path: 'form-templates',
            name: 'FormList',
            redirect: '/events'
          },
          {
            path: 'form-builder/:formId',
            name: 'FormBuilderWrapper',
            redirect: '/events',
            props: true
          },
          {
            path: 'form-create',
            name: 'FormCreate',
            redirect: '/events'
          },
          {
            path: 'filler-slide',
            name: 'FillerSlide',
            redirect: '/events'
          }
        ]
      },
      {
        path: 'reports-new',
        name: 'ReportsNew',
        component: () => import(/* webpackChunkName: "StudioReports" */ '@/studio/reports/ReportsLayout.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: 'cross-event',
            name: 'CrossEventReport',
            component: () => import('@/studio/reports/CrossEventReport.vue')
          },
          {
            path: 'roi',
            name: 'RoiReport',
            component: () => import('@/studio/reports/RoiReport.vue')
          },
          {
            path: 'custom-report',
            name: 'CustomReport',
            component: () => import('@/studio/reports/CustomReportWrapper.vue'),
            children: [
              {
                path: 'search',
                name: 'SearchEmbed',
                component: () => import('@/studio/reports/SearchEmbed.vue')
              },
              {
                path: 'saved-reports',
                name: 'SavedReports',
                component: () => import('@/studio/reports/SavedReports.vue')
              },
              {
                path: ':liveboardID',
                name: 'CustomLiveboard',
                component: () => import('@/studio/reports/CustomLiveboard.vue')
              }
            ]
          }
        ]
      },
      {
        path: 'settings',
        name: 'Settings-old',
        redirect: '/settings-new',
        meta: { requiresAuth: true },
        children: [
          {
            path: 'domain',
            name: 'Domain',
            redirect: '/settings-new/domains'
          },
          {
            path: 'tags',
            name: 'Tags',
            redirect: '/events'
          },
          {
            path: 'contract-details',
            name: 'ContractDetails',
            redirect: '/settings-new/organization'
          },
          {
            path: 'tokens',
            name: 'Tokens',
            redirect: '/settings-new/tokens'
          }
        ]
      },
      {
        path: 'settings-new',
        name: 'Settings',
        meta: { requiresAuth: true },
        component: () => import(/* webpackChunkName: "StudioSettings" */ '@/studio/settings/SettingsLayout.vue'),
        children: [
          {
            path: 'users',
            name: 'OrgUserList',
            component: () => import('@/studio/settings/OrgMembersSettings/OrgUserList.vue')
          },
          {
            path: 'permissions',
            name: 'Permissions',
            component: () => import('@/studio/settings/Permissions/Permissions.vue')
          },
          {
            path: 'domains',
            name: 'DomainSettings',
            component: () => import('@/studio/settings/DomainSettings/DomainSettings.vue')
          },
          {
            path: 'tokens',
            name: 'TokensSettings',
            component: () => import('@/studio/settings/TokensSettings/TokensSettings.vue')
          },
          {
            path: 'organization',
            name: 'OrganizationSettings',
            component: () => import('@/studio/settings/OrganizationSettings/OrganizationSettings.vue')
          },
          {
            path: 'preferences',
            name: 'Preferences',
            component: () => import('@/studio/settings/EmojisSettings/EmojisSettings.vue')
          },
          {
            path: 'certificates',
            name: 'Certificates',
            component: () =>
              import(
                /* webpackChunkName: "CertificatesSettings" */ '@/studio/settings/CertificateSettings/CertificatesSettings.vue'
              ),
            beforeEnter: (to, from, next) => {
              if (!store.getters['org/getCurrentOrg']?.enableCertificateBuilder) {
                return next({ name: 'Settings' });
              }
              next();
            }
          },
          {
            path: 'certificates/builder',
            name: 'CertificateBuilder',
            component: () =>
              import(
                /* webpackChunkName: "CertificateBuilder" */ '@/studio/settings/CertificateSettings/CertificateBuilder/CertificateBuilder.vue'
              ),
            beforeEnter: (to, from, next) => {
              if (!store.getters['org/getCurrentOrg']?.enableCertificateBuilder) {
                return next({ name: 'Settings' });
              }
              next();
            }
          },
          {
            path: 'teams',
            name: 'Teams',
            component: () => import('@/studio/workspace/Workspaces.vue')
          },
          {
            path: 'forms',
            name: 'Forms',
            component: () => import('@/studio/forms/Forms.vue')
          },
          {
            path: 'forms/:formId',
            name: 'FormDetails',
            component: () => import('@/studio/forms/viewForm/ViewFormLayout.vue')
          },
          {
            path: 'forms/:formId/:integrationType',
            name: 'FormIntegration',
            component: () => import('@/studio/forms/formFieldMapping/FormIntegrationLayout.vue'),
            children: [
              {
                path: 'field-mapping/:crmObjectType',
                name: 'FormFieldMapping',
                component: () => import('@/studio/forms/formFieldMapping/FormFieldMapping.vue')
              }
            ]
          },
          /* redirect route handling for old paths */
          {
            path: 'members',
            name: 'OrgMembersSettings',
            redirect: 'users'
          },
          {
            path: 'contract',
            name: 'ContractSettings',
            redirect: 'organization'
          }
        ]
      },
      {
        path: 'settings/tags/:integrationName',
        name: 'OrgIntegrationSolution',
        redirect: '/events',
        meta: { requiresAuth: true }
      },
      {
        path: 'integrations',
        name: 'Integrations',
        component: () => import('@/studio/orgIntegration/OrgIntegration.vue'),
        meta: { requiresAuth: true },
        children: [
          {
            path: 'applications',
            name: 'Applications',
            component: () => import('@/studio/orgIntegration/applications/OrgIntegrationSolutionList.vue')
          },
          {
            path: 'applications/:instanceID/settings',
            name: 'SolutionSettings',
            component: () => import('@/studio/orgIntegration/applications/OrgIntegrationSolutionSettings.vue'),
            children: [
              {
                path: 'field-mapping/:crmObjectType',
                name: 'FieldMapping',
                component: () => import('@/studio/orgIntegration/fieldMapping/FieldMapping.vue')
              },
              {
                path: 'marketo-base-program',
                name: 'MarketoBaseProgram',
                component: () => import('@/studio/orgIntegration/marketoBaseProgram/MarketoBaseProgram.vue')
              },
              {
                path: 'eloqua-event-form',
                name: 'EloquaEventFormConnection',
                component: () => import('@/studio/orgIntegration/eloquaEventForm/EloquaEventForm.vue')
              },
              {
                path: 'sync-preferences',
                name: 'SyncPreferenceDrawer',
                component: () => import('@/studio/orgIntegration/syncPreference/SyncPreferenceDrawer.vue')
              }
            ]
          },
          {
            path: 'applications/:instanceID/authentication',
            name: 'SolutionAuthentication',
            component: () => import('@/studio/orgIntegration/solutionAuthentication/SolutionAuthentication.vue')
          },
          {
            path: 'applications/:instanceID/authentication/test',
            name: 'SolutionAuthenticationTest',
            component: () => import('@/studio/orgIntegration/solutionAuthentication/OrgTestIntegration.vue')
          },
          {
            path: 'authentications',
            name: 'Authentications',
            component: () => import('@/studio/orgIntegration/authentications/OrgIntegrationAuthenticationList.vue')
          },
          {
            path: 'authentications/:authenticationID/settings',
            name: 'AuthenticationSettings',
            component: () => import('@/studio/orgIntegration/authentications/OrgIntegrationAuthenticationSettings.vue')
          },
          {
            path: 'javascript-embeds',
            name: 'JavascriptEmbeds',
            component: () => import('@/studio/orgIntegration/javascriptEmbeds/JavascriptEmbeds.vue')
          },
          {
            path: 'javascript-embeds/:instanceID',
            name: 'JavascriptEmbedSettings',
            component: () => import('@/studio/orgIntegration/javascriptEmbeds/JavascriptEmbedSettings.vue')
          }
        ]
      },
      {
        path: '/stage/:eventId',
        name: 'StageWrapper',
        redirect: '/events',
        meta: { requiresAuth: true },
        children: [
          {
            path: '',
            name: 'EventsWrapper',
            redirect: '/events'
          },
          {
            path: 'setup',
            name: 'Setup',
            redirect: '/events'
          },
          {
            path: 'branding',
            name: 'Branding',
            redirect: '/events'
          },
          {
            path: 'registration',
            name: 'Registration',
            redirect: '/events'
          },
          {
            path: 'registrants',
            name: 'Registrants',
            redirect: '/events'
          },
          {
            path: 'registrantsel',
            name: 'RegistrantsEl',
            redirect: '/events'
          },
          {
            path: 'emails',
            name: 'Emails',
            redirect: '/events',
            children: [
              {
                path: 'custom-template',
                name: 'CustomEmail',
                redirect: '/events'
              }
            ]
          }
        ]
      },
      {
        path: 'connected-apps',
        name: 'ConnectedApps',
        redirect: '/events'
      },
      {
        path: 'connected-apps/:instanceID',
        name: 'SolutionSettings-old',
        redirect: '/events'
      },
      {
        path: 'connected-apps/:instanceID/webhook/:webhookID',
        name: 'WebhookAddEdit',
        redirect: '/events'
      },
      {
        path: 'space',
        name: 'Space',
        redirect: '/events',
        children: [
          {
            path: 'agenda',
            name: 'agenda',
            redirect: '/events'
          },
          {
            path: 'rooms',
            name: 'rooms',
            redirect: '/events'
          },
          {
            path: 'booths',
            name: 'booths',
            redirect: '/events'
          },
          {
            path: 'speakers',
            name: 'speakers',
            redirect: '/events'
          },
          {
            path: 'attendees',
            name: 'attendees',
            redirect: '/events'
          },
          {
            path: 'engagementPanel',
            name: 'engagementPanel',
            redirect: '/events'
          }
        ]
      },
      {
        path: 'analytics',
        name: 'Analytics',
        redirect: '/events'
      }
    ]
  },
  {
    path: '/shared/clip/:clipId',
    name: 'ContentLabSharedClipPage',
    component: () => import('@/studio/shared/ContentLabSharedClipPage.vue')
  },
  {
    path: '/shared/post/:postId',
    name: 'ContentLabSharedPostPage',
    component: () => import('@/studio/shared/ContentLabSharedPostPage.vue')
  },
  {
    path: '/:catchAll(.*)', // Define a param named "catchAll" with a custom regexp
    redirect: {
      name: 'Error',
      params: {
        code: '404'
      }
    },
    component: () => import('@/views/Error.vue'),
    meta: { requiresAuth: true }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.NODE_ENV === 'production' ? '' : ''),
  routes
});

router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (from.fullPath === '/') {
      // Page Reload
    } else if (to.fullPath.startsWith('/content/') && from.fullPath.startsWith('/content')) {
      // Landing on content lab from within content lab
    } else if (to.fullPath.startsWith('/content/') && !from.fullPath.startsWith('/content')) {
      // Landing on content lab from studio
      sessionStorage.setItem('contentLabLoadedFrom', from.fullPath);
    } else {
      // Landing in studio
      sessionStorage.setItem('contentLabLoadedFrom', '');
    }

    if (to.fullPath.startsWith('/home/') && from.fullPath.startsWith('/stage/')) {
      store.commit('analytics/setUserGroupToken', '');
      store.commit('studioEventList/setCurrentEventID', '');
      store.commit('branding/clearLocalChanges');
    }

    if (
      to.fullPath.startsWith('/events/') ||
      (to.fullPath.startsWith('/eventHub/') && from.fullPath.startsWith('/event/'))
    ) {
      store.commit('analytics/setUserGroupToken', '');
      store.commit('studioEventList/setCurrentEventID', '');
      store.commit('branding/clearLocalChanges');
    }

    if (!store.state.user.currentUser) {
      return next('/auth/login');
    } else {
      const user = store.state.user.currentUser;

      if (showNoWorkspacePage(store, to)) {
        return next('/no-workspace');
      }

      if (!user.organization) {
        if (!to.fullPath.startsWith('/content/auth/already-logged-in') && !to.fullPath.startsWith('/createorg')) {
          return next('/content/auth/already-logged-in');
        }
      } else {
        if (to.fullPath.startsWith('/content/auth/already-logged-in') || to.fullPath.startsWith('/createorg')) {
          return next('/home');
        }

        if (user.role === UserRole.WORKSPACE_ADMIN && to.fullPath.startsWith('/settings-new/tokens')) {
          return next('/home');
        }
      }
    }
  } else {
    if (to.name === 'RegistrantsActivity') {
      return next();
    }
    if (
      (to.fullPath.startsWith('/auth/') || to.fullPath.startsWith('/content/auth/')) &&
      !(
        to.name === 'fromMagicLink' ||
        to.name === 'Oktalogin' ||
        to.name === 'Ssologin' ||
        to.name === 'External' ||
        to.name === 'OrgLogin'
      )
    ) {
      if (store.state.user.currentUser && !store.state.user.currentUser.organization) {
        const queryString = to.fullPath.split('?')[1];
        return next('/content/auth/already-logged-in' + (queryString ? `?${queryString}` : ''));
      }

      if (store.state.user.currentUser) {
        const redirect = to.query.redirect || '';
        const hash = fireActionFromHash(to.hash);
        const redirectUrl = redirect ? redirect + hash : '';
        return next(redirectUrl ? `/${redirectUrl}` : '/home');
      }
    }

    if (to.name === 'Login' && from.name === 'RegistrantsActivity') {
      if (to.query.redirect) return next();
      return next(`/auth/login?redirect=${from.fullPath}`);
    }
  }
  return next();
});

router.afterEach((to, from) => {
  analytics.page(to.name);
  analytics.track('Route change', {
    from: from.fullPath,
    fromName: from.name,
    to: to.fullPath,
    toName: to.name
  });
});

export default router;
/* c8 ignore stop */
